/**
 * The Gallery's images and the batch being made, kept outside React.
 *
 * A batch takes up to a minute an image. Held in the page it would stop when
 * the person looked at a chat meanwhile; held here it goes on, and the page
 * finds it where it left it. Module memory, as in useTurns: a reload starts
 * again from the list the Worker keeps.
 */

import { useSyncExternalStore } from 'react';
import {
  ApiError,
  deleteGalleryImage,
  galleryThread,
  listGallery,
  listVideoJobs,
  startVideo,
  streamGallery,
  type GalleryItem,
  type GalleryRequest,
  type HubServerFrame,
  type ImageModelKey,
  type UsageSummary,
  type VideoJob,
  type VideoRequest
} from '../api';
import { rootOf, withoutImage, withRevision, type Threads } from './galleryThreads';
import { withVideoEnded, withVideosResynced } from './galleryVideos';

/** One image of the running batch: still coming, or not coming at all. */
export type Slot = 'making' | 'failed' | 'done';

export interface Running {
  /** When the batch started, in ms. */
  since: number;
  count: number;
  width: number;
  height: number;
  /** The prompt as rewritten, once it is. */
  used: string | null;
  slots: Slot[];
  /** Why a failed slot failed, by its index. */
  failures: Record<number, string>;
}

export interface GalleryState {
  items: GalleryItem[];
  next: string | null;
  loaded: boolean;
  loading: boolean;
  running: Running | null;
  error: string | null;
  /** Fetched threads, by their original's id. */
  threads: Threads;
  /** The change being made, by the thread it goes in. */
  changing: { rootId: string; prompt: string } | null;
  /** Why the last change did not come, by its thread. */
  changeError: { rootId: string; message: string } | null;
  /** Videos being made, oldest first. */
  videos: VideoJob[];
}

let state: GalleryState = {
  items: [],
  next: null,
  loaded: false,
  loading: false,
  running: null,
  error: null,
  threads: {},
  changing: null,
  changeError: null,
  videos: []
};
let abort: AbortController | null = null;
const listeners = new Set<() => void>();

function set(patch: Partial<GalleryState> | ((s: GalleryState) => Partial<GalleryState>)) {
  state = { ...state, ...(typeof patch === 'function' ? patch(state) : patch) };
  for (const l of listeners) l();
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export function useGallery(): GalleryState {
  return useSyncExternalStore(subscribe, () => state);
}

const message = (err: unknown) => (err instanceof ApiError ? err.message : 'Something went wrong. Try again.');

/** The first page, once; later calls do nothing. */
export async function loadGallery(): Promise<void> {
  if (state.loaded || state.loading) return;
  set({ loading: true });
  try {
    const [page, made] = await Promise.all([listGallery(), listVideoJobs().catch(() => ({ jobs: [] as VideoJob[] }))]);
    set({ items: page.items, next: page.next, videos: made.jobs, loaded: true, loading: false });
  } catch (err) {
    set({ loading: false, error: message(err) });
  }
}

export async function loadMore(): Promise<void> {
  if (!state.next || state.loading) return;
  set({ loading: true });
  try {
    const page = await listGallery(state.next);
    set((s) => ({ items: [...s.items, ...page.items.filter((i) => !s.items.some((x) => x.id === i.id))], next: page.next, loading: false }));
  } catch (err) {
    set({ loading: false, error: message(err) });
  }
}

/** Make a batch. `onUsage` hears what it cost, for the app's usage meter. */
export async function createImages(
  request: GalleryRequest,
  references: File[],
  onUsage: (usage: UsageSummary) => void
): Promise<void> {
  if (state.running) return;
  abort = new AbortController();
  set({ error: null, running: { since: Date.now(), count: request.count, width: 0, height: 0, used: null, slots: Array(request.count).fill('making'), failures: {} } });
  const slot = (index: number, value: Slot) =>
    set((s) => (s.running ? { running: { ...s.running, slots: s.running.slots.map((x, i) => (i === index ? value : x)) } } : {}));
  try {
    await streamGallery(
      request,
      references,
      (e) => {
        switch (e.type) {
          case 'prompt':
            set((s) => (s.running ? { running: { ...s.running, used: e.used } } : {}));
            break;
          case 'start':
            set((s) => (s.running ? { running: { ...s.running, width: e.width, height: e.height } } : {}));
            break;
          case 'image':
            set((s) => ({ items: [e.item, ...s.items] }));
            slot(e.index, 'done');
            break;
          case 'failed':
            set((s) => (s.running ? { running: { ...s.running, failures: { ...s.running.failures, [e.index]: e.message } } } : {}));
            slot(e.index, 'failed');
            break;
          case 'usage':
            onUsage(e.usage);
            break;
          case 'error':
            set({ error: e.message });
            break;
          case 'done':
            break;
        }
      },
      abort.signal
    );
  } catch (err) {
    set({ error: message(err) });
  } finally {
    abort = null;
    // Failed slots stay until the next batch, so a gap says what happened;
    // slots a Stop left unmade go.
    set((s) => {
      const slots = s.running?.slots.map((x): Slot => (x === 'making' ? 'done' : x)) ?? [];
      return { running: s.running && slots.includes('failed') ? { ...s.running, slots } : null };
    });
  }
}

/** Stops the batch. What has landed stays; the rest is not made. */
export function stopImages(): void {
  abort?.abort();
}

/** Clears what a finished batch left behind: its failed slots. */
export function clearFinished(): void {
  if (!abort) set({ running: null });
}

/** Deletes an image: an original with its thread, a revision alone. */
export async function removeImage(item: GalleryItem): Promise<void> {
  const before = { items: state.items, threads: state.threads };
  set((s) => withoutImage(s.items, s.threads, item));
  try {
    await deleteGalleryImage(item.id);
  } catch (err) {
    set({ ...before, error: message(err) });
  }
}

/** Fetches an original's thread, once. */
export async function loadThread(original: GalleryItem): Promise<void> {
  if (state.threads[original.id] || !original.revisions) return;
  try {
    const { items } = await galleryThread(original.id);
    set((s) => ({ threads: { ...s.threads, [original.id]: items } }));
  } catch (err) {
    set({ error: message(err) });
  }
}

/** "Change this…": one image made from `parent`, kept as its revision. */
export async function changeImage(
  parent: GalleryItem,
  prompt: string,
  model: ImageModelKey,
  onUsage: (usage: UsageSummary) => void
): Promise<void> {
  if (state.changing) return;
  const rootId = rootOf(parent);
  set({ changing: { rootId, prompt }, changeError: null });
  const fail = (text: string) => set({ changeError: { rootId, message: text } });
  try {
    await streamGallery(
      { prompt, model, aspect: parent.aspect, count: 1, improve: false, parent: parent.id },
      [],
      (e) => {
        if (e.type === 'image') set((s) => withRevision(s.items, s.threads, e.item));
        else if (e.type === 'failed' || e.type === 'error') fail(e.message);
        else if (e.type === 'usage') onUsage(e.usage);
      }
    );
  } catch (err) {
    fail(message(err));
  } finally {
    set({ changing: null });
  }
}

/** Start a video; the account's hub says when it is done (`videoEnded`). */
export async function makeVideo(request: VideoRequest): Promise<void> {
  set({ error: null });
  try {
    const job = await startVideo(request);
    set((s) => ({ videos: [...s.videos.filter((j) => j.id !== job.id), job] }));
  } catch (err) {
    set({ error: message(err) });
  }
}

/** The hub's word that a video is done or failed. */
export function videoEnded(frame: Extract<HubServerFrame, { t: 'video' }>): void {
  set((s) => {
    const next = withVideoEnded(s, frame);
    return { items: next.items, videos: next.videos, ...(next.error ? { error: next.error } : {}) };
  });
}

/** The hub reconnected: any `video` frame sent meanwhile was missed, so the
 *  videos being made and the newest page are asked for again. */
export async function resyncVideos(): Promise<void> {
  if (!state.loaded) return;
  try {
    const [made, page] = await Promise.all([listVideoJobs(), listGallery()]);
    set((s) => withVideosResynced(s, made.jobs, page.items));
  } catch {
    // The next reconnect or a reload tries again.
  }
}
